// Section card rendered inline in the canvas. Clickable to select
// (highlights the strip + reveals section properties on the right).
// Each block inside is editable using the same BlockNode the main
// canvas uses — so the user gets a consistent editing experience
// regardless of which section a block lives in.

import { useRef, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'

import BlockNode from './BlockNode'
import SlashMenu from './SlashMenu'
import { PopupTriggerPreview } from './renderers'
import { colorSchemeToCssVars, resolveColorScheme } from './tokens'
import { blockStyleCss, sectionDefaultInset } from './theme'
import type { Block, BlockType, DesignTokens, PageInput, Section, Selection } from './types'

interface Props {
  section: Section
  selected: boolean
  selection: Selection
  inputs: PageInput[]
  inputValues: Record<string, unknown>
  // Imperative setter forwarded to BlockNode → embed live preview, so
  // row selection in an embedded transaction list writes the row's id
  // / data into page variables (selectedIdVar / selectedDataVar).
  onSetInputValue?: (name: string, value: unknown) => void
  project: string
  // Page-level design tokens (color scheme library). Used to resolve
  // `section.colorSchemeId` so the section's wrapper paints the chosen
  // scheme's background/text and exposes its colors as CSS vars to every
  // block inside — mirrors the Angular runtime's `sectionCssVars`.
  designTokens?: DesignTokens
  onSelectSection: () => void
  onSelectBlock: (blockId: string) => void
  // A link inside an already-selected block was activated — the canvas
  // defers the actual navigation (with a leave-the-designer prompt) to the
  // page owner. Unselected blocks just select on link click (no nav).
  onActivateLink?: (href: string, newTab: boolean) => void
  onUpdateBlock: (next: Block) => void
  onInsertBlockAt: (index: number, type: BlockType) => void
  onDeleteBlock: (blockId: string) => void
  onRemoveSection?: () => void
  // Optional rich-edit handlers — for the Main section we pass through
  // the existing canvas's slash-menu / drag-and-drop / Enter-to-insert
  // logic. For Header/Footer we omit them.
  onTextKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>, block: Block) => void
  onTransform?: (block: Block, anchor: { left: number; top: number; bottom: number }) => void
  onDragStart?: (e: React.DragEvent, blockId: string) => void
  onDragOver?: (e: React.DragEvent, blockId: string) => void
  onDrop?: (e: React.DragEvent, blockId: string) => void
  onDragEnd?: () => void
  // Section-level dragover/drop fallback — catches drops in dead space
  // (above the first block, below the last block, the section's padding,
  // the "Add block" pill area) so they still resolve using the last
  // hovered block indicator instead of bouncing back as "Drop cancelled".
  onSectionDragOver?: (e: React.DragEvent) => void
  onSectionDrop?: (e: React.DragEvent) => void
  dropTarget?: { id: string; pos: 'before' | 'after' } | null
}

const TAG_LABEL: Record<NonNullable<Section['tag']>, string> = {
  header: 'Header',
  main: 'Main content',
  footer: 'Footer',
  aside: 'Aside',
  popup: 'Pop-up',
  custom: 'Section',
}

const TAG_COLOR: Record<NonNullable<Section['tag']>, string> = {
  header: '#3b82f6',
  main: '#10b981',
  footer: '#6366f1',
  aside: '#a855f7',
  popup: '#14b8a6',
  custom: '#8b5cf6',
}

export function SectionStrip({
  section, selected, selection,
  inputs, inputValues, project,
  designTokens,
  onSelectSection, onSelectBlock, onActivateLink, onUpdateBlock,
  onInsertBlockAt, onRemoveSection,
  onTextKeyDown, onTransform, onDragStart, onDragOver, onDrop, onDragEnd,
  onSectionDragOver, onSectionDrop,
  dropTarget,
  onSetInputValue,
}: Props) {
  const tag = (section.tag ?? 'main') as NonNullable<Section['tag']>
  const ringColor = TAG_COLOR[tag]
  const isMain = tag === 'main'

  // Section color-scheme override — when set, re-publishes the scheme's
  // palette as `--pg-*` CSS vars on the section wrapper so every block
  // inside picks up the override (matches the Angular runtime's behavior).
  const scheme = resolveColorScheme(designTokens, section.colorSchemeId)
  const schemeVars = scheme ? colorSchemeToCssVars(scheme) : null

  return (
    <div
      onClick={(e) => {
        // Click on the section background (not on a block) selects the section
        if (e.target === e.currentTarget) onSelectSection()
      }}
      onDragOver={onSectionDragOver}
      onDrop={onSectionDrop}
      style={{
        position: 'relative',
        margin: '0 -16px 0 -16px',
        padding: '8px 16px 16px',
        borderRadius: 8,
        outline: selected ? `2px solid ${ringColor}` : '2px solid transparent',
        outlineOffset: 2,
        // Background priority: selection tint > scheme background > transparent.
        // Selection tint preserves the visual cue that *this* section is being
        // edited even when a dark scheme is applied.
        background: selected
          ? `${ringColor}10`
          : (scheme?.background ?? 'transparent'),
        color: scheme?.text,
        transition: 'outline 0.12s ease, background 0.12s ease',
        // Per-section override for `--pg-block-gap` so e.g. the header can
        // be tighter or the footer airier than the page default.
        ...(typeof section.blockGap === 'number'
          ? ({ ['--pg-block-gap' as never]: `${Math.max(0, section.blockGap)}px` })
          : {}),
        // Scheme CSS vars cascade to every block inside this section.
        ...(schemeVars ?? {}),
      }}
    >
      {!isMain && (
        <div
          onClick={(e) => { e.stopPropagation(); onSelectSection() }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 10px', marginBottom: 8,
            background: ringColor, color: '#fff',
            borderRadius: 6, cursor: 'pointer',
            fontSize: 13, fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.04em',
            userSelect: 'none',
          }}
        >
          <span>{TAG_LABEL[tag]}</span>
          <span style={{ opacity: 0.7, fontWeight: 400, fontSize: 13 }}>
            {section.blocks.length} block{section.blocks.length === 1 ? '' : 's'} • Click to edit settings
          </span>
          <span style={{ flex: 1 }} />
          {onRemoveSection && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                if (window.confirm(`Remove ${TAG_LABEL[tag]}?`)) onRemoveSection()
              }}
              title={`Remove ${TAG_LABEL[tag]}`}
              style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
                padding: 4, borderRadius: 4, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center',
              }}
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      )}

      {tag === 'popup' && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '8px 12px', marginBottom: 8, borderRadius: 6,
            fontSize: 13, lineHeight: 1.4,
            background: 'rgba(20,184,166,0.10)', color: 'var(--color-text-secondary)',
            border: '1px dashed #14b8a6',
          }}
        >
          <div style={{ flex: 1 }}>
            🪟 These blocks open in a <strong>pop-up modal</strong> at runtime — add them like any
            section. Click the section header to configure the trigger.
          </div>
          {/* Live, non-interactive preview of the configured trigger so the
              author can fine-tune its look without entering preview mode. */}
          {section.popup?.trigger && (
            <div
              title="Live trigger preview"
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                flexShrink: 0, paddingLeft: 12, borderLeft: '1px solid rgba(20,184,166,0.35)',
              }}
            >
              <PopupTriggerPreview trigger={section.popup.trigger} />
              <span style={{ fontSize: 13, opacity: 0.7 }}>preview</span>
            </div>
          )}
        </div>
      )}

      {/* The section's own background / padding, drawn the way the live
          renderers draw it, so a spacing change is visible while editing. */}
      <div style={{
        display: 'flex', flexDirection: 'column',
        ...(section.style ? blockStyleCss(section.style) : {}),
        ...sectionDefaultInset(section, !!scheme),
      }}>
        {section.blocks.length === 0 ? (
          <div style={emptyStyle}>
            Empty {TAG_LABEL[tag].toLowerCase()} — click <em>Add block</em> below.
          </div>
        ) : (
          section.blocks.map((block, i) => {
            const isSelected = selection.type === 'block'
              && selection.blockId === block.id
              && (selection.sectionId === section.id || !selection.sectionId)
            return (
            <div
              key={block.id}
              data-block-id={block.id}
              className="page-block-wrap"
              // dragover/drop are attached at the wrap level so drops in the
              // padding around a block (the visual gap) still resolve to
              // that block — otherwise a drop landing in the gap silently
              // fails because no element receives the drop event.
              onDragOver={onDragOver ? (e) => onDragOver(e, block.id) : undefined}
              onDrop={onDrop ? (e) => onDrop(e, block.id) : undefined}
              onDragEnd={onDragEnd}
              onMouseEnter={(e) => {
                const handles = e.currentTarget.querySelector('.block-handles') as HTMLElement | null
                if (handles) handles.style.opacity = '1'
              }}
              onMouseLeave={(e) => {
                const handles = e.currentTarget.querySelector('.block-handles') as HTMLElement | null
                // Hide handles entirely when the cursor leaves the block —
                // they reveal on hover (mouseenter sets opacity to 1) and
                // shouldn't bleed visual noise into the canvas otherwise.
                if (handles) handles.style.opacity = '0'
              }}
              style={{
                position: 'relative',
                // Block spacing comes from `theme.layout.blockGap` exposed as
                // `--pg-block-gap` on the canvas wrapper. Half the gap goes
                // above and half below each wrap so the visual spacing
                // between any two adjacent blocks equals one full gap, and
                // the wrap's hit area covers the entire gap (so drops in
                // the gap still land on a real drop target).
                paddingTop: i === 0 ? 0 : 'calc(var(--pg-block-gap, 12px) / 2)',
                paddingBottom: i === section.blocks.length - 1 ? 0 : 'calc(var(--pg-block-gap, 12px) / 2)',
              }}
            >
              <BlockNode
                block={block}
                index={i}
                selected={isSelected}
                inputs={inputs}
                inputValues={inputValues}
                project={project}
                onSelect={() => onSelectBlock(block.id)}
                onActivateLink={onActivateLink}
                onChange={onUpdateBlock}
                onTextKeyDown={onTextKeyDown ?? (() => { /* noop */ })}
                onInsertAfter={() => onInsertBlockAt(i + 1, 'paragraph')}
                onTransform={onTransform ? (anchor) => onTransform(block, anchor) : (() => { /* noop */ })}
                onDragStart={onDragStart ? (e) => onDragStart(e, block.id) : (() => { /* noop */ })}
                onDragOver={onDragOver ? (e) => onDragOver(e, block.id) : (() => { /* noop */ })}
                onDrop={onDrop ? (e) => onDrop(e, block.id) : (() => { /* noop */ })}
                onDragEnd={onDragEnd ?? (() => { /* noop */ })}
                canDrag={Boolean(onDragStart)}
                onInputValueChange={() => { /* */ }}
                onSetInputValue={onSetInputValue}
                showDropIndicator={dropTarget?.id === block.id ? dropTarget.pos : null}
              />
              {/* Delete affordance is intentionally only on the property
                  panel (right side) — keeping it off the canvas avoids
                  accidental clicks while editing block content. */}

              {/* "Insert block below" — visible only when this block is the
                  selected one. Same SlashMenu picker as the section-level
                  Add block button, but inserts at i+1 so the new block lands
                  immediately after the current selection. */}
              {isSelected && (
                <div style={{
                  display: 'flex', justifyContent: 'center',
                  marginTop: 'calc(var(--pg-block-gap, 12px) / 2)',
                }}>
                  <AddBlockButton
                    label="Insert block below"
                    onPick={(type) => onInsertBlockAt(i + 1, type)}
                  />
                </div>
              )}
            </div>
            )
          })
        )}

        <AddBlockButton onPick={(type) => onInsertBlockAt(section.blocks.length, type)} />
      </div>
    </div>
  )
}

/* ─── Add-block trigger: opens the same SlashMenu used by `/` in the canvas ─ */

function AddBlockButton({
  onPick,
  label = 'Add block',
}: {
  onPick: (type: BlockType) => void
  label?: string
}) {
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const [anchor, setAnchor] = useState<{ left: number; top: number; bottom: number } | null>(null)
  return (
    <div style={{ position: 'relative', alignSelf: 'center' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          if (anchor) { setAnchor(null); return }
          const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect()
          setAnchor({ left: r.left, top: r.top, bottom: r.bottom })
        }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 14px', fontSize: 13, fontWeight: 500,
          border: '1px dashed var(--pg-border)', borderRadius: 999,
          background: 'var(--pg-bg)',
          color: 'var(--pg-text-secondary)', cursor: 'pointer',
        }}
      >
        <Plus size={13} /> {label}
      </button>
      {anchor && (
        <SlashMenu
          query=""
          anchor={anchor}
          showSearch
          onPick={(type) => { onPick(type); setAnchor(null) }}
          onClose={() => setAnchor(null)}
        />
      )}
    </div>
  )
}

const emptyStyle: React.CSSProperties = {
  fontSize: 13, color: 'var(--pg-text-secondary)',
  padding: 14, textAlign: 'center',
  background: 'var(--pg-surface)', borderRadius: 6,
  border: '1px dashed var(--pg-border)',
}
